import React from 'react'
import moment from 'moment'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'

import withPermissionCheck from '../../core/access-control/with-permission-check'
import {
  Permission,
  roleType,
  isSbjUser as isSbjRole,
} from '../../core/auth0'

import {
  emergenciesActions,
  Emergency,
  EmergenciesParam,
  EmergenciesFilters,
} from '../../core/emergencies'
import { Station, stationsActions } from '../../core/stations'

import RootState from '../../core/root-state'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import { strings } from '../components/emergencies/emergencies-content'
import styles from '../styles/modules/emergencies/emergencies.module.scss'
import EmergenciesTable from '../components/emergencies/emergencies-item'
import Pagination from '../components/pagination/pagination'
import QueryDate from '../components/emergencies/query-date'
import QueryStation from '../components/emergencies/query-station'
import Button from '../components/button'

interface Props {
  role: roleType
  emergencies: Emergency[]
  stations: Station[]
  filters: EmergenciesFilters
  isLoading: boolean
  stationsLoading: boolean
  totalCount: number
  currentPage: number
  pageSize: number
  fetchStations: () => void
  fetchEmergencies: (params?: EmergenciesParam) => void
  resetEmergencies: () => void
  setFilters: (filters: EmergenciesFilters) => void
  setCurrentPage: (page: number) => void
}

interface State {
  readonly dateFrom: Date | null
  readonly dateTo: Date | null
  readonly stationId: string
  readonly pushed: boolean
  readonly hasSearched: boolean
}

class EmergenciesScreen extends React.Component<Props, State> {
  readonly state: State = {
    dateFrom: null,
    dateTo: null,
    stationId: '@all',
    pushed: false,
    hasSearched: false,
  }

  componentDidMount = () => {
    this.props.fetchStations()
    const stationIdValue = '@all'

    this.setState({ hasSearched: true, stationId: stationIdValue })

    const filters = this.buildFilters(null, null, stationIdValue)
    this.props.setFilters(filters)
    this.props.setCurrentPage(1)
    this.props.resetEmergencies()
    this.fetchWithFilters(filters, 0)
  }

  componentDidUpdate = (prevProps: Props) => {
    if (prevProps.isLoading && !this.props.isLoading && this.state.pushed) {
      this.setState({ pushed: false })
    }

  }

  private get isSbjUser(): boolean {
    return isSbjRole(this.props.role)
  }

  private normalizeDate = (date: Date | null): Date | null => {
    if (!date) {
      return null
    }
    const normalized = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    )
    const today = new Date()
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    )
    return normalized > todayStart ? todayStart : normalized
  }

  private convertDateToString = (date: Date): string =>
    moment(date).format('YYYY-MM-DD')

  private buildFilters = (
    from: Date | null,
    to: Date | null,
    stationIdValue: string
  ): EmergenciesFilters => {
    const parsedStationId =
      stationIdValue === '@all'
        ? null
        : parseInt(stationIdValue, 10)
    const normalizedStationId = Number.isNaN(parsedStationId)
      ? null
      : parsedStationId
    return {
      dateFrom: from ? this.convertDateToString(from) : null,
      dateTo: to ? this.convertDateToString(to) : null,
      stationId: normalizedStationId,
    }
  }

  private fetchWithFilters = (
    filters: EmergenciesFilters,
    offset: number
  ) => {
    const params: EmergenciesParam = {
      count: this.props.pageSize,
      offset,
      station_id: filters.stationId ?? undefined,
    }

    if (filters.dateFrom) {
      params.reported_at_from = filters.dateFrom
    }

    if (filters.dateTo) {
      params.reported_at_to = filters.dateTo
    }

    this.props.fetchEmergencies(params)
  }

  private handleOnChangeFrom = (from: Date | null) => {
    const normalizedFrom = this.normalizeDate(from)
    this.setState(prevState => {
      if (!normalizedFrom) {
        return {
          ...prevState,
          dateFrom: null,
          pushed: false,
        }
      }

      const nextDateTo =
        prevState.dateTo && normalizedFrom > prevState.dateTo
          ? normalizedFrom
          : prevState.dateTo

      return {
        ...prevState,
        dateFrom: normalizedFrom,
        dateTo: nextDateTo ?? null,
        pushed: false,
      }
    })
  }

  private handleOnChangeTo = (to: Date | null) => {
    const normalizedTo = this.normalizeDate(to)
    this.setState(prevState => {
      if (!normalizedTo) {
        return {
          ...prevState,
          dateTo: null,
          pushed: false,
        }
      }

      const nextDateFrom =
        prevState.dateFrom && normalizedTo < prevState.dateFrom
          ? normalizedTo
          : prevState.dateFrom

      return {
        ...prevState,
        dateFrom: nextDateFrom ?? null,
        dateTo: normalizedTo,
        pushed: false,
      }
    })
  }

  private handleStationChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const stationId = event.target.value
    this.setState({ stationId, pushed: false })
  }

  handlePageChange = (page: number) => {
    this.props.setCurrentPage(page)
    const offset = (page - 1) * this.props.pageSize
    this.fetchWithFilters(this.props.filters, offset)
  }

  handleSearch = () => {
    const { dateFrom, dateTo, stationId } = this.state
    const filters = this.buildFilters(dateFrom, dateTo, stationId)
    this.setState({ pushed: true, hasSearched: true })
    this.props.resetEmergencies()
    this.props.setCurrentPage(1)
    this.props.setFilters(filters)
    this.fetchWithFilters(filters, 0)
  }

  render() {
    const {
      emergencies,
      totalCount,
      currentPage,
      pageSize,
      isLoading,
      stations,
      stationsLoading,
    } = this.props
    const { dateFrom, dateTo, stationId, pushed, hasSearched } = this.state
    const hasEmergencies = emergencies.length > 0
    const canSearch = !isLoading
    const showInitialMessage = !hasSearched

    return (
      <Layout location={'emergencies'}>
        <Wrapper title={strings.title}>
          <div className={styles.container}>
            <div className={styles.queryContainer}>
              <QueryDate
                from={dateFrom}
                to={dateTo}
                onChangeFrom={this.handleOnChangeFrom}
                onChangeTo={this.handleOnChangeTo}
              />
              <QueryStation
                isLoading={stationsLoading}
                options={stations}
                value={stationId}
                onChange={this.handleStationChange}
              />
              <Button
                title={strings.queryButton}
                onClick={this.handleSearch}
                className={styles.queryButton}
                isLoading={isLoading && pushed}
                disabled={!canSearch}
              />
            </div>
            <div className={styles.tableContainer}>
              {showInitialMessage ? (
                <p className={styles.emptyMessage}>
                  {strings.initialEmptyMessage}
                </p>
              ) : hasEmergencies ? (
                <>
                  <EmergenciesTable emergencies={emergencies} />
                  <Pagination
                    currentPage={currentPage}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    onPageChange={this.handlePageChange}
                  />
                </>
              ) : (
                <p className={styles.emptyMessage}>{strings.emptyTableMessage}</p>
              )}
            </div>
          </div>
        </Wrapper>
      </Layout>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  role: state.auth0.role,
  emergencies: state.emergencies.emergencies,
  stations: state.stations.stations,
  filters: state.emergencies.filters,
  isLoading: state.emergencies.isLoading,
  stationsLoading: state.stations.isLoading,
  totalCount: state.emergencies.totalCount,
  currentPage: state.emergencies.currentPage,
  pageSize: state.emergencies.pageSize,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchStations: () => dispatch(stationsActions.fetchStations()),
  fetchEmergencies: (params?: EmergenciesParam) =>
    dispatch(emergenciesActions.fetchEmergencies(params)),
  resetEmergencies: () => dispatch(emergenciesActions.resetEmergencies()),
  setFilters: (filters: EmergenciesFilters) =>
    dispatch(emergenciesActions.setFilters(filters)),
  setCurrentPage: (page: number) =>
    dispatch(emergenciesActions.setCurrentPage(page)),
})

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(EmergenciesScreen)

export default withPermissionCheck(ConnectedComponent, {
  requiredPermission: Permission.EMERGENCY_HISTORY_READ,
  redirectTo: '/404',
})
